import { useState, ReactNode, PropsWithChildren } from "react";
import { View, Pressable, StyleSheet, StyleProp, ViewStyle, LayoutChangeEvent } from "react-native";
import Animated, { useAnimatedStyle, useSharedValue } from "react-native-reanimated";
import { Resizer } from "@/components/ui/Resizer";
import { useTheme } from "@/hooks/useTheme";
import { useStorage } from "@/hooks/useStorage";

type FlyoutProps = PropsWithChildren & {
  layout?: "vertical" | "horizontal";
  storageKey?: string;
  resizable?: boolean;
  children?: ReactNode;
  setActive?: React.Dispatch<boolean>;
  max?: number;
  min?: number;
  visible?: boolean;
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  onUnmount?: () => void;
  rootStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
};

export default function Flyout({
  visible: controlledVisible,
  setVisible: setControlledVisible,
  resizable = true,
  min = 25,
  max = 99,
  storageKey = "flyout",
  children,
  onUnmount,
  rootStyle,
  style,
}: FlyoutProps) {
  const isControlled = controlledVisible !== undefined && setControlledVisible !== undefined;
  const [uncontrolledVisible, setUncontrolledVisible] = useState(false);
  const visible = isControlled ? controlledVisible : uncontrolledVisible;
  const setVisible = isControlled ? setControlledVisible : setUncontrolledVisible;
  const storage = useStorage();
  const savedWidth = parseInt(storage.getItem(storageKey)) as number;

  const width = useSharedValue<number>(resizable ? (savedWidth ?? 50) : min);
  if (width.get() > max) storage.saveFast(storageKey, `${max}`);
  if (isNaN(width.get())) width.set(min);
  const [rootLayout, setRootLayout] = useState<LayoutChangeEvent>();
  const animatedStyle = useAnimatedStyle(() => ({
    // flexBasis: `${width.get()}%`,
    flexBasis: `${width.get()}%`,
  }));

  const { colors } = useTheme();

  return (
    visible && (
      <View
        onLayout={(e) => {
          setRootLayout(e);
        }}
        style={[
          StyleSheet.absoluteFill,
          styles.container,
          styles.layout,
          { backgroundColor: colors.backdrop },
          rootStyle,
        ]}
      >
        <Pressable
          style={[styles.filter]}
          onPress={() => {
            setVisible(false);
            onUnmount?.();
          }}
        />
        {resizable && (
          <Resizer
            min={min}
            max={max}
            parentLayout={rootLayout}
            shared={width}
            onEnd={() => storage.saveFast(storageKey, width.get().toString())}
          />
        )}
        <Animated.View
          style={[
            animatedStyle,
            styles.view,
            {
              minWidth: 200,
              backgroundColor: colors.backgroundSecondary,
              borderColor: colors.border,
            },
            style,
          ]}
        >
          {children}
        </Animated.View>
      </View>
    )
  );
}

const styles = StyleSheet.create({
  container: {
    zIndex: 2,
    elevation: 2,
  },
  heder: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    zIndex: 2,
    elevation: 2,
  },
  filter: {
    flex: 1,
  },
  layout: {
    flex: 1,
    flexDirection: "row",
  },
  view: {
    boxShadow: "#000",
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    backfaceVisibility: "hidden",
  },
});
